import axios from 'axios';
import i18next from 'i18next';
import { Category } from 'src/models';
import { getRootStoreSingleton } from 'src/models/root-store/root-store-singleton';

import { DEFAULT_AXIOS_CONFIG } from './axiosConfig';

// TypeScript 타입 확장
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retryCount?: number;
  }
}

// 최대 재시도 횟수
const MAX_RETRY_COUNT = 3;

// refresh 엔드포인트 — 요청/응답 인터셉터의 가드에 공통 사용한다.
// (실제 엔드포인트는 /v1/auth/refresh 이므로 endsWith 로 매칭)
const REFRESH_PATH = '/auth/refresh';

// Axios 인스턴스 생성
const axiosInstance = axios.create(DEFAULT_AXIOS_CONFIG);

// === 토큰 갱신 중복 방지 (refresh storm 방지) ===
// 동시에 여러 요청이 401을 받아도 단일 refresh Promise를 공유한다.
// (jjtp api-client 의 isRefreshing/refreshPromise 패턴을 MST authStore 에 맞게 이식)
function requestRefresh() {
  return getRootStoreSingleton().authStore.loginRefresh();
}

let refreshPromise: ReturnType<typeof requestRefresh> | null = null;

function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = requestRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// === 세션 만료 처리 1회 보장 ===
// 동시 요청이 모두 401→refresh 실패를 받으면 각자 스낵바+로그아웃을 호출해
// "세션이 만료되었습니다" 알림이 N번 뜬다. 최초 1회만 처리하도록 가드한다.
// (로그인/재인증으로 access token이 다시 생기면 가드 해제)
let sessionExpiredHandled = false;

function handleSessionExpired() {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;

  const snackbarStore = getRootStoreSingleton().snackbarStore;
  const authStore = getRootStoreSingleton().authStore;
  snackbarStore.setProps({
    open: true,
    category: Category.Error,
    title: i18next.t('common.sessionExpired'),
  });
  authStore.initAuth();
}

// 정상 로그인/토큰 회전 시 다음 만료를 다시 안내할 수 있도록 가드 해제
function resetSessionExpiredGuard() {
  sessionExpiredHandled = false;
}

// 요청 인터셉터 설정
axiosInstance.interceptors.request.use(
  (config) => {
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.increaseLoading(); // T029: Use counter-based loading state

    // refresh 요청은 바디의 refresh token만으로 동작하는 엔드포인트.
    // 만료된 access token을 Authorization 헤더로 함께 보내면 BE 검증 단계에서
    // 거부될 수 있으므로, /auth/refresh 에는 Authorization 헤더를 붙이지 않는다.
    const isRefreshRequest = config.url?.endsWith(REFRESH_PATH);

    const token = getRootStoreSingleton().authStore.getAccessToken();
    if (token && !isRefreshRequest) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else if (isRefreshRequest) {
      delete config.headers['Authorization'];
    }
    config.headers['Accept-Language'] =
      localStorage.getItem('i18nextLng') || i18next.language || 'ko';
    return config;
  },
  (error) => {
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.decreaseLoading(); // T031: Decrement counter on request error
    return Promise.reject(error);
  },
);

// 응답 인터셉터 설정
axiosInstance.interceptors.response.use(
  async (response) => {
    const data = response.data;
    const config = response.config;

    const authStore = getRootStoreSingleton().authStore;
    const snackbarStore = getRootStoreSingleton().snackbarStore;
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.decreaseLoading(); // T030: Decrement counter on successful response

    // 응답 헤더로 새 access token이 내려오면 즉시 회전(silent rotation)한다.
    // BE가 해당 헤더를 내려주지 않으면 undefined → no-op.
    const rotatedToken = response.headers?.['x-new-access-token'];
    if (typeof rotatedToken === 'string' && rotatedToken) {
      authStore.setAccessToken(rotatedToken);
    }

    // 정상 응답이 왔다는 건 인증이 유효하다는 뜻 — 다음 만료를 다시 안내하도록 가드 해제.
    if (data?.resultCode === 'S') {
      resetSessionExpiredGuard();
    }

    // 로그인 시도 자체의 실패(잘못된 자격증명 등)는 정상적인 흐름이므로
    // 전역 에러 팝업을 띄우지 않고 호출한 화면(Login)이 메시지를 처리하도록 위임.
    const isLoginRequest = config.url?.endsWith('/auth/login');

    if (data.resultCode === 'F' && !isLoginRequest) {
      if (
        (data.errorCode === '401' || data.errorCode === '4001') &&
        !config.url?.endsWith(REFRESH_PATH)
      ) {
        // 재시도 카운터 증가
        config._retryCount = (config._retryCount || 0) + 1;

        // 최대 재시도 횟수 초과 시 로그아웃 처리 (세션 만료 1회 처리)
        if (config._retryCount > MAX_RETRY_COUNT) {
          handleSessionExpired();
          return response;
        }

        // 토큰 갱신(중복 방지: 동시 401은 단일 refresh 공유)
        const result = await refreshAccessToken();
        if (result?.resultCode === 'S') {
          resetSessionExpiredGuard();
          config.headers['Authorization'] = `Bearer ${authStore.getAccessToken()}`;
          config.headers['Accept-Language'] =
            localStorage.getItem('i18nextLng') || i18next.language || 'ko';
          return axiosInstance(config);
        } else {
          // 토큰 갱신 실패: 서버 에러 메시지 대신 일반 메시지 사용 (보안)
          if (process.env.NODE_ENV === 'development' && result?.errorMessage) {
            // eslint-disable-next-line no-console
            console.warn('[Auth] Token refresh failed:', result.errorMessage);
          }
          handleSessionExpired();
        }
      } else if (
        data.errorCode !== '409' &&
        data.errorCode !== 'CAI-7002' && // 로그인ID 중복 (helperText로 처리)
        data.errorCode !== 'CAI-7003' // 닉네임 중복 (helperText로 처리)
      ) {
        // 비즈니스 에러는 서버 메시지 사용, 없으면 일반 메시지
        const serverMessage = data?.errorMessage || data?.err_msg;

        // 개발 환경에서만 상세 로그 출력
        if (process.env.NODE_ENV === 'development' && serverMessage) {
          // eslint-disable-next-line no-console
          console.warn('[API Error]', data.errorCode, serverMessage);
        }

        snackbarStore.setProps({
          open: true,
          category: Category.Error,
          title: serverMessage || i18next.t('common.commonError'),
        });
      }
    }

    return response;
  },
  async (error) => {
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.decreaseLoading(); // T031: Decrement counter on error response

    const snackbarStore = getRootStoreSingleton().snackbarStore;
    const authStore = getRootStoreSingleton().authStore;
    const config = error.config;

    // 로그인 시도 자체의 실패는 호출 화면(Login)이 처리하므로 전역 핸들링/팝업 스킵.
    if (config?.url?.endsWith('/auth/login')) {
      return Promise.reject(error);
    }

    // HTTP 401 Unauthorized 처리 - 토큰 갱신 시도
    if (error.response?.status === 401 && config && !config.url?.endsWith(REFRESH_PATH)) {
      // 재시도 카운터 증가
      config._retryCount = (config._retryCount || 0) + 1;

      // 최대 재시도 횟수 초과 시 로그아웃 처리 (세션 만료 1회 처리)
      if (config._retryCount > MAX_RETRY_COUNT) {
        handleSessionExpired();
        return Promise.reject(error);
      }

      // 토큰 갱신 시도 (중복 방지: 동시 401은 단일 refresh 공유)
      try {
        const result = await refreshAccessToken();
        if (result?.resultCode === 'S') {
          resetSessionExpiredGuard();
          // 원래 요청 재시도
          config.headers['Authorization'] = `Bearer ${authStore.getAccessToken()}`;
          config.headers['Accept-Language'] =
            localStorage.getItem('i18nextLng') || i18next.language || 'ko';
          return axiosInstance(config);
        } else {
          // 토큰 갱신 실패
          if (process.env.NODE_ENV === 'development' && result?.errorMessage) {
            // eslint-disable-next-line no-console
            console.warn('[Auth] Token refresh failed on 401:', result.errorMessage);
          }
          handleSessionExpired();
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // 토큰 갱신 중 에러 발생
        handleSessionExpired();
        return Promise.reject(error);
      }
    }

    if (error.code === 'ERR_NETWORK') {
      snackbarStore.setProps({
        open: true,
        category: Category.Error,
        title: i18next.t('common.serverError'),
      });
    } else if (error.code === 'ECONNABORTED') {
      snackbarStore.setProps({
        open: true,
        category: Category.Error,
        title: i18next.t('common.commonError'),
      });
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
